/**
 * Share button — uses the Web Share API on mobile, falls back to copying the
 * link on desktop. The URL passed in must be the `/api/share/...` endpoint on
 * the backend so social crawlers (X, Facebook, Instagram, LinkedIn, iMessage)
 * hit the Open Graph HTML and render a rich preview card.
 */
import { useState } from "react";
import { Share2, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ShareButton({ shareUrl, title, description, className = "", testid = "share-btn", variant = "default", accent = "", outline = false, iconClass = "h-4 w-4", kind = "" }) {
    const [copied, setCopied] = useState(false);

    const track = () => {
        const m = (shareUrl || "").match(/\/share\/([^/]+)\/([^/?#]+)/);
        api.post("/analytics/track", { type: "share", kind: kind || (m ? m[1] : ""), ref_id: m ? m[2] : "" }).catch(() => {});
    };

    const onShare = async () => {
        // Prefer the OS share sheet on mobile / supported browsers.
        if (navigator.share) {
            try {
                await navigator.share({ title, text: description, url: shareUrl });
                track();
                return;
            } catch (err) {
                if (err && err.name === "AbortError") return; // user cancelled — no toast
                // fall through to clipboard fallback
            }
        }
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            track();
            toast.success("Link copied to clipboard");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Final fallback: prompt the user to copy manually.
            window.prompt("Copy this link:", shareUrl);
        }
    };

    const hasSize = /(?:^|\s)text-(?:xs|sm|base|lg|xl)(?:\s|$)/.test(className);
    const hasHeight = /(?:^|\s)h-(?:\d+|px|full|screen)(?:\s|$)/.test(className);
    const base = `rounded-none uppercase tracking-wider inline-flex items-center justify-center gap-2 ${hasHeight ? "" : "h-10"} px-4 transition-colors font-semibold ${hasSize ? "" : "text-xs"}`;
    const accentStyle = accent
        ? (outline ? { backgroundColor: "#141414", borderColor: accent, color: accent } : { backgroundColor: accent, borderColor: accent, color: "#fff" })
        : undefined;
    const styles = accent
        ? "border hover:opacity-90"
        : variant === "primary"
            ? "bg-[#22c55e] border border-[#22c55e] text-black hover:bg-[#16a34a] hover:border-[#16a34a]"
            : "bg-[#141414] border border-[#2a2a2a] text-white hover:border-[#ff4500] hover:text-[#ff4500]";

    return (
        <button
            type="button"
            data-testid={testid}
            onClick={onShare}
            style={accentStyle}
            className={`${base} ${styles} ${className}`}
        >
            {copied ? <Check className={iconClass} /> : <Share2 className={iconClass} />}
            {copied ? "Copied" : "Share"}
        </button>
    );
}
